class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    
    this.mode = this.dataset.mode || 'infinite';
    this.anchor = this.querySelector("a");
    
    if (!this.anchor) return;

    this.isLoading = false;
    this.setupLoadMore();
  }

  setupLoadMore() {
    if (this.mode === 'click') {
      this.setupClickMode();
    } else {
      this.setupInfiniteMode();
    }
  }

  setupClickMode() {
    this.anchor.style.display = "inline-flex";
    this.anchor.style.pointerEvents = "auto";
    this.originalText = this.anchor.innerText || "Load More";
    
    this.anchor.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.isLoading) {
        this.loadNextPage();
      }
    });
  }

  setupInfiniteMode() {
    this.anchor.style.display = "none";
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && !window.vhCollectionRestoring) {
          this.loadNextPage();
        }
      });
    }, {
      rootMargin: '100px'
    });

    this.observer.observe(this);
  }

  async loadNextPage({ html: cachedHtml } = {}) {
    if (this.isLoading) return;
    
    this.isLoading = true;

    if (this.mode === 'click') {
      this.anchor.style.display = "inline-flex";
      this.anchor.innerText = "Loading...";
      this.anchor.classList.add("loading");
      this.anchor.style.pointerEvents = "none";
    } else {
      this.anchor.style.display = "flex";
      this.anchor.style.width = "400px";
      this.anchor.innerText = "Loading...";
    }

    const url = this.anchor.getAttribute("href");
    if (!url) {
      this.isLoading = false;
      return;
    }

    try {
      let text = cachedHtml;
      if (typeof text !== 'string') {
        const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        text = await response.text();
      }
      const html = new DOMParser().parseFromString(text, "text/html");

      const newGrid = html.querySelector("[data-product-grid]");
      const grid = document.querySelector("[data-product-grid]");

      if (newGrid && grid) {
        const newProducts = Array.from(newGrid.children);
        
        const productElements = newProducts.filter(child => {
          if (child.tagName === 'INFINITE-SCROLL') return false;
          if (child.classList.contains('collection__featured')) return false;
          if (child.classList.contains('collection__list-image')) return true;
          if (child.tagName === 'A' && child.querySelector('.collection__list-image')) return true;
          if (child.classList.contains('product-item')) return true;
          if (child.querySelector('.product-item')) return true;
          return false;
        });
        
        productElements.forEach((child) => {
          const clonedChild = child.cloneNode(true);
          grid.appendChild(clonedChild);
        });

        const loadedPages = JSON.parse(grid.dataset.vhLoadedPages || '[]');
        grid.dataset.vhLoadedPages = JSON.stringify([...loadedPages, new URL(url, location.href).href]);

        grid.offsetHeight;

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('products:loaded'));
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            detail: { 
              sectionId: new URLSearchParams(url.split('?')[1]).get('section_id') 
            }
          }));
          
          this.initializeReviews();
        }, 100);
      }

      const newInfinite = html.querySelector("infinite-scroll");
      if (newInfinite) {
        newInfinite.dataset.mode = this.mode;
        this.replaceWith(newInfinite);
      } else {
        this.remove();
      }
      return Boolean(newGrid && grid);
    } catch (err) {
      if (this.mode === 'click') {
        this.anchor.innerText = this.originalText || "Load More";
        this.anchor.style.pointerEvents = "auto";
        this.anchor.classList.remove("loading");
      } else {
        this.anchor.style.display = "none";
      }
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  initializeReviews() {
    setTimeout(() => {
      if (typeof window.richSnippets === 'function') {
        try {
          window.richSnippets();
        } catch (error) {}
      } else if (typeof window.loadReviewsIoRatingSnippets === 'function') {
        try {
          window.loadReviewsIoRatingSnippets();
        } catch (error) {}
      } else if (window.RukRatingSnippets && typeof window.RukRatingSnippets.init === 'function') {
        try {
          window.RukRatingSnippets.init();
        } catch (error) {}
      }
    }, 500);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

customElements.define("infinite-scroll", InfiniteScroll);
